import {createStyles, Switch} from '@mantine/core';
import React, {useState} from 'react';

import {PipelineStepDataKind, PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import StepTypeMenu from 'pipelines/components/StepTypeMenu';
import {stepTypeIsConfigEditorSupported} from 'pipelines/generalized-step-config-editor/stepTypeIsConfigEditorSupported';
import isStepTypeEnabledForUser from 'pipelines/PipelineCapabilities';
import {getLatestStepVersion, getOutputStepDatas, getSourceStepTypes} from 'pipelines/StepConnectionUtils';
import {MenuPanelSection} from 'ui-library/menus/MenuPanel';
import PlainSearchInput, {BorderRadius, ClearType} from 'ui-library/PlainSearchInput';

/**
 * Step types which cannot be added from the right sidebar, even though the step type has a UI
 * schema and the `gating` section of the step type's YAML manifest allows the user to create it.
 *
 * It is not necessary to add a step type to this constant if the step type will already be hidden
 * for one of these reasons:
 * - `userCanCreateDirectly` in the step type manifest is `False`
 * - `configFlags` in the step type manifest requires a flag that is always disabled
 * - The step type does not have a UI schema
 */
const STEP_TYPES_THAT_CANNOT_BE_ADDED_FROM_RIGHT_SIDEBAR: ReadonlySet<PipelineStepType> = new Set([
  // Currently, CHART_VIEW steps can only be added from the left sidebar.
  PipelineStepType.CHART_VIEW,
]);

/**
 * Check if the user has access to add a step type and the step type has a config UI.
 */
export function canAddStepType(stepType: PipelineStepType): boolean {
  return (
    stepTypeIsConfigEditorSupported(stepType, getLatestStepVersion(stepType)) &&
    isStepTypeEnabledForUser(stepType) &&
    !STEP_TYPES_THAT_CANNOT_BE_ADDED_FROM_RIGHT_SIDEBAR.has(stepType)
  );
}

/**
 * Filter function for source step types that can be added.
 */
export function filterSourceStepTypes(stepType: PipelineStepType): boolean {
  const sourceStepTypes = getSourceStepTypes();
  return canAddStepType(stepType) && sourceStepTypes.includes(stepType);
}

/** Step types that cannot be used as replacements for a table source. */
const NON_REPLACEABLE_STEP_TYPES: Partial<Record<PipelineStepType, boolean>> = {
  // Template variables are not a data source — they cannot replace a table.
  [PipelineStepType.DEFINE_TEMPLATE_VARIABLES]: true,
};

/**
 * Filter function for source step types that can replace a table source.
 * Excludes file-producing step types (since the output type must match) and
 * step types that are not meaningful as table replacements.
 */
export function filterReplaceableSourceStepTypes(stepType: PipelineStepType): boolean {
  if (!filterSourceStepTypes(stepType)) {
    return false;
  }
  if (NON_REPLACEABLE_STEP_TYPES[stepType]) {
    return false;
  }
  // Only allow step types that produce dataset (table) output, not file output.
  const outputDatas = getOutputStepDatas(stepType);
  return outputDatas.length > 0 && outputDatas.every((o) => o.kind === PipelineStepDataKind.DATASET);
}

const useStyles = createStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  searchBar: {
    flexShrink: 0,
    paddingBottom: '12px',
  },
  searchInput: {
    width: '100%',
  },
  menuWrapper: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    minHeight: 0,
  },
  footer: {
    flexShrink: 0,
    padding: '8px',
    display: 'flex',
    justifyContent: 'flex-end',
  },
});

interface StepTypeSelectorProps {
  selectedStepType: PipelineStepType | null;
  onSelectStepType: (stepType: PipelineStepType | null) => void;
  extraSections?: Array<MenuPanelSection>;
  /**
   * Optional override for the step type filter. If not provided, defaults to `filterSourceStepTypes`
   * which filters to source step types that can be added via the config editor.
   */
  filterStepTypes?: (stepType: PipelineStepType) => boolean;
}

/**
 * Shared step type selection UI used by both AddStepRightSidebar and ReplaceSourceStepRightSidebar.
 *
 * Renders a search bar, StepTypeMenu with source step types, and a descriptions toggle.
 */
export default function StepTypeSelector({
  selectedStepType,
  onSelectStepType,
  extraSections,
  filterStepTypes,
}: StepTypeSelectorProps): JSX.Element {
  const {classes} = useStyles();
  const [showDescriptions, setShowDescriptions] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  return (
    <div className={classes.container}>
      <div className={classes.searchBar}>
        <PlainSearchInput
          placeholder="Search..."
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
          }}
          onEscape={() => {
            setSearchQuery('');
          }}
          clearType={ClearType.X}
          borderRadius={BorderRadius.LARGE}
          className={classes.searchInput}
        />
      </div>
      <div className={classes.menuWrapper}>
        <StepTypeMenu
          filterStepTypes={filterStepTypes ?? filterSourceStepTypes}
          selectedStepType={selectedStepType}
          onSelectStepType={onSelectStepType}
          showDescription={showDescriptions}
          searchQuery={searchQuery}
          extraSections={extraSections}
        />
        <div className={classes.footer}>
          <Switch
            label="Step descriptions"
            checked={showDescriptions}
            onChange={(e) => {
              setShowDescriptions(e.currentTarget.checked);
            }}
            styles={{
              root: {justifyContent: 'flex-end'},
              body: {alignItems: 'center'},
            }}
          />
        </div>
      </div>
    </div>
  );
}
